import { NgRedux } from '@angular-redux/store';
import {Hotel} from '../../model/hotel/hotel.model';
import {HotelRegion} from '../../model/hotel/hotel-region.model';
import {HotelAddress} from '../../model/hotel/hotel-address.model';
import {mapToDateTimeFromString, useDefault, useFirst} from '../../../common/common';
import {HotelImage} from '../../model/hotel/hotel-image.model';
import {HotelExtraFeatureType} from '../../model/hotel/hotel-extra-feature-type.model';
import {HotelExtraFeature} from '../../model/hotel/hotel-extra-feature.model';
import {Brand} from 'ihg-brand-common-app-brand-constants';
import { getAllianceResort,getAnaHotelsBrand } from '../../../common';
import { IAppState } from '../../../store';

const PAL_BRAND_CODE = 'MAMI';
const PAL_BRAND_NAME = 'IHG Army Hotels';

export class HotelModelBuilder {
  private _model: Hotel;
  constructor() {
    this._model = new Hotel();
  }

  overrideBrandNames(chainCode: string = ''): string {
    const brandObject = Brand.FIXED_BRAND_NAMES.find(brand => brand.brandCode === chainCode.toLowerCase());
    if (!!brandObject) {
      return brandObject.brandName;
    }
  }

  withHotelData(hotelInfo: any): HotelModelBuilder {
    const hotelData = useDefault(hotelInfo.hotelInfo, {});
    if (hotelData.brandInfo) {
      this._model.mnemonic = hotelData.brandInfo.mnemonic;
      this._model.brandCode = hotelData.brandInfo.brandCode;
      this._model.futureBrandInfo = useDefault(hotelData.brandInfo.futureBrandInfo, null);
      if (hotelData.brandInfo.brandCode === PAL_BRAND_CODE) {
          this._model.brandName = PAL_BRAND_NAME;
      } else {
          if (hotelData.brandInfo.brandCode) {
              if (hotelData.brandInfo.brandCode.toLowerCase() === getAllianceResort()) {
                  this._model.brandName = hotelData.brandInfo.brandName.replace("-", "");
              } else if (hotelData.brandInfo.brandCode.toLowerCase() === getAnaHotelsBrand()) {
                  this._model.brandName = hotelData.brandInfo.brandName.split("-").reverse().join(" ").trim();
              } else {
                  this._model.brandName = this.overrideBrandNames(hotelData.brandInfo.chainCode) || hotelData.brandInfo.brandName;
              }
          }
      }
      this._model.chainCode = hotelData.brandInfo.chainCode;
    }
    if (hotelData.address) {
      this._model.address = new HotelAddress(hotelData.address);
    }

    if (hotelData.contact && hotelData.contact.length) {
      this._model.frontDeskNumber = useDefault(hotelData.contact[0].frontDeskNumber, '');
    }

    if (hotelData.profile) {
      this._model.name = useDefault(hotelData.profile.name, '');
      this._model.shortDescription = useDefault(hotelData.profile.shortDescription, '');
      this._model.seoCity = useDefault(hotelData.profile.seoCity, null);
      this._model.region = HotelRegion.fromString(hotelData.profile.marketingRegion);
      this._model.latitude = useDefault(hotelData.profile.latitude, 0);
      this._model.longitude = useDefault(hotelData.profile.longitude, 0);
      this._model.vatIncluded = useDefault(hotelData.profile.vatIncluded, false);
      if (hotelData.profile.primaryImageUrl
        && hotelData.profile.primaryImageUrl.formats
        && hotelData.profile.primaryImageUrl.formats.length) {
        for (const format of hotelData.profile.primaryImageUrl.formats) {
          this._model.images.push(new HotelImage(format.url, format.aspectHeight, format.aspectWidth));
        }
      }
    }
    const policiesData = useDefault(hotelData.policies, {});
    this._model.checkInTime = mapToDateTimeFromString(policiesData.checkinTime);
    this._model.checkOutTime = mapToDateTimeFromString(policiesData.checkoutTime);
    if (hotelData.profile) {
      this._model.rating = useDefault(hotelData.profile.averageReview, 0);
    } else {
      this._model.rating = 0;
    }
    return this;
  }

  withHotelExtraPoliciesData(hotelExtraInfo: any): HotelModelBuilder {
    const parkingData = useDefault(hotelExtraInfo.parking, {});
    const valetData = useFirst(parkingData.valet, null);
    const dailyParkingData = useFirst(parkingData.daily, null);
    const petPolicyData = useFirst(hotelExtraInfo.petPolicy, null);
    const wirelessData = useDefault(useDefault(useDefault(hotelExtraInfo.internet, [])
      .find((element) => {
        return element.category === 'Guest Room'
      }), {}).locations, []).find((element) => {
      return element.description === 'Wireless Data Connection';
    });
    if (dailyParkingData) {
      this._model.extraFeatures.set(HotelExtraFeatureType.PARKING, new HotelExtraFeature(HotelExtraFeatureType.PARKING, dailyParkingData.description, useDefault(dailyParkingData.fee, null)));
    }
    if (valetData) {
      this._model.extraFeatures.set(HotelExtraFeatureType.VALET, new HotelExtraFeature(HotelExtraFeatureType.VALET, valetData.description, useDefault(valetData.fee, null)));
    }
    if (wirelessData) {
      this._model.extraFeatures.set(HotelExtraFeatureType.WIRELESS, new HotelExtraFeature(HotelExtraFeatureType.WIRELESS, wirelessData.description, useDefault(wirelessData.fee, null)));
    }
    if (petPolicyData) {
      this._model.extraFeatures.set(HotelExtraFeatureType.PETS, new HotelExtraFeature(HotelExtraFeatureType.PETS, petPolicyData.description, null));
    }
    return this;
  }

  build(): Hotel {
    return this._model;
  }
}



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/api/services/builders/hotel.model.builder.ts